
import { computed, defineComponent, ref, watch } from 'vue';
import { RuntimeO2MField } from '@kunlun/dependencies';
import {
  buildDefaultIndicator,
  buildDefaultRange,
  CHANNEL_INDICATOR_OPTIONS,
  formatRange,
  IIndicator,
  INDICATOR_NAME_LABEL_MAP,
  INDICATOR_NAME_OPTIONS,
  isBracket,
  normalizeEnumValue,
  OPERATOR_OPTIONS,
  RangeOperator
} from './constants';

export default defineComponent({
  name: 'EvaluationIndicatorTable',
  props: {
    formData: {
      type: Object,
      default: () => ({})
    },
    change: {
      type: Function,
      default: () => {}
    },
    _field: {
      type: Object as () => RuntimeO2MField,
      default: () => ({} as RuntimeO2MField)
    },
    isReadonly: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const indicators = ref<IIndicator[]>([]);

    const fieldName = () => (props._field && props._field.name) || 'indicators';

    /** 归一化来自后端/表单的数据 */
    const normalize = (list: any[]): IIndicator[] => {
      if (!Array.isArray(list) || !list.length) {
        return [];
      }
      return list.map((item) => {
        const ranges = Array.isArray(item?.ranges) ? item.ranges : [];
        return {
          indicatorName: normalizeEnumValue(item?.indicatorName),
          ranges: ranges.length
            ? ranges.map((r: any) => ({
                operator: normalizeEnumValue(r?.operator) || RangeOperator.GE,
                minValue: r?.minValue ?? null,
                maxValue: r?.maxValue ?? null,
                score: r?.score ?? null
              }))
            : [buildDefaultRange()]
        };
      });
    };

    /** 序列化回提交结构：非区间运算符清空 maxValue */
    const serialize = (list: IIndicator[]) =>
      list.map((ind) => ({
        indicatorName: ind.indicatorName,
        ranges: (ind.ranges || []).map((r) => ({
          operator: r.operator,
          minValue: r.minValue,
          maxValue: isBracket(r.operator) ? r.maxValue : null,
          score: r.score
        }))
      }));

    const emitChange = () => {
      if (props.isReadonly) {
        return;
      }
      props.change(serialize(indicators.value));
    };

    const onOperatorChange = (range: any) => {
      if (!isBracket(range.operator)) {
        range.maxValue = null;
      }
      emitChange();
    };

    const addRange = (indicator: IIndicator) => {
      indicator.ranges.push(buildDefaultRange());
      emitChange();
    };

    const removeRange = (indicator: IIndicator, rIndex: number) => {
      if (indicator.ranges.length <= 1) {
        return;
      }
      indicator.ranges.splice(rIndex, 1);
      emitChange();
    };

    const addIndicator = () => {
      indicators.value.push(buildDefaultIndicator());
      emitChange();
    };

    const removeIndicator = (gIndex: number) => {
      indicators.value.splice(gIndex, 1);
      emitChange();
    };

    // 初始化：从表单数据同步（只读模式下持续同步，因不会回写不存在覆盖问题；
    // 编辑模式下拿到首个非空值后停止，避免回写覆盖编辑）
    const stopInit = watch(
      () => props.formData?.[fieldName()],
      (val) => {
        if (Array.isArray(val) && val.length) {
          indicators.value = normalize(val);
          if (!props.isReadonly) {
            stopInit();
          }
        }
      },
      { deep: true, immediate: true }
    );

    // 指标下拉选项按适用渠道过滤：供应链/闪电仓各一套；未选渠道时全量兼容旧数据
    const indicatorNameOptions = computed(() => {
      const channel = normalizeEnumValue(props.formData?.channel);
      return CHANNEL_INDICATOR_OPTIONS[channel] || INDICATOR_NAME_OPTIONS;
    });

    return {
      indicators,
      indicatorNameOptions,
      indicatorNameLabelMap: INDICATOR_NAME_LABEL_MAP,
      operatorOptions: OPERATOR_OPTIONS,
      isBracket,
      formatRange,
      emitChange,
      onOperatorChange,
      addRange,
      removeRange,
      addIndicator,
      removeIndicator
    };
  }
});
